import { get } from 'lodash';
import join from 'lodash/fp/join';
import { InMemoryWebStorage, Log, User, UserManager, UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts';
import { OAuthConfigSettings } from 'src/main';

import { configureEnv, env } from '../../env';
import { configureStorage, RouteStorage } from './storage';
import { AuthenticatedUserWithProfile, MappedUserProfile, mapUserProfile, OICDUserProfile } from './userProfile';

const trace = env.featureToggles.tracing
    ? // eslint-disable-next-line no-console
      (...args: unknown[]) => console.log(`[oidcLogin]`, ...args)
    : () => {};

const getWindow = () => (typeof window === 'undefined' ? ({} as Window & typeof globalThis) : window);

const param = (window: Window, regex: RegExp, defaultValue: string | null = null) => {
    let result = defaultValue;
    decodeURI(window.location.href).replace(regex, (_, it: string) => {
        result = it;
        return result;
    });
    return result;
};

const pullLocale = (result: AuthenticatedUserWithProfile) => get(result, 'profile.locale', 'en-GB');

const getErrorMessage = (error: unknown) => {
    if (error instanceof Error) {
        return error.message;
    }

    if (typeof error === 'string') {
        return error;
    }

    return '';
};

const isExpectedSilentRenewError = (error: unknown) => {
    const message = getErrorMessage(error).toLowerCase();

    return (
        message.includes('login_required') ||
        message.includes('interaction_required') ||
        message.includes('consent_required') ||
        message.includes('oauth 2.0 parameter: prompt') ||
        message.includes('prompt')
    );
};

export type UserPublishedInfo = {
    accessToken: string;
    expiresInSeconds: number;
    idToken: OICDUserProfile;
    locale: string;
    profile: MappedUserProfile;
};

const adaptPublishedInfo = (result: AuthenticatedUserWithProfile): UserPublishedInfo => {
    return {
        accessToken: result.access_token,
        expiresInSeconds: result.expires_in ?? 0,
        idToken: result.profile,
        locale: pullLocale(result),
        profile: mapUserProfile(result.profile),
    };
};

const configureAuth = (window: Window & typeof globalThis, importMetaEnv: ImportMetaEnv) => {
    const { runtimeConfig } = configureEnv(window, importMetaEnv);

    const redirectUri = runtimeConfig.login.redirectUri;
    const silentRedirectUri = runtimeConfig.login.silentRedirectUri;

    const settings: UserManagerSettings = {
        authority: `${runtimeConfig.login.authority}`,
        client_id: `${runtimeConfig.login.clientId}`,
        loadUserInfo: false,
        redirect_uri: `${redirectUri}`,
        response_type: 'code',
        scope: join(' ', runtimeConfig.login.oauthScope),
        silent_redirect_uri: `${silentRedirectUri || redirectUri}`,
        includeIdTokenInSilentRenew: false,
        automaticSilentRenew: true,
        monitorSession: true,
        staleStateAgeInSeconds: 600,
        filterProtocolClaims: false,
        userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
    };

    trace('oidc.auth.settings', settings);

    return new UserManager(settings);
};

export const configureSetupOAuth = (
    auth: UserManager,
    storage: RouteStorage,
    window: Window & typeof globalThis,
    importMetaEnv: ImportMetaEnv
) => {
    const { featureToggles, runtimeConfig } = configureEnv(window, importMetaEnv);

    if (featureToggles.tracing) {
        Log.setLogger(console);
        Log.setLevel(Log.DEBUG);
    }

    const isFreshRedirect = Boolean(param(window, /.*code=([^&]+)/u));

    const saveCurrentRoute = () => {
        const initialRoute = [window.location.hash, window.location.search].join('').replace(/^#/u, '');

        storage.saveRoute(initialRoute);

        trace('saving initial route', initialRoute);
    };

    return (config: OAuthConfigSettings) => {
        const trySignin = () =>
            auth
                .signinSilent()
                .then((result: User | null) => {
                    if (result === null) {
                        throw new Error('Silent signin returned no user');
                    }

                    if (result.expires_in === undefined) {
                        throw new Error('Silent signin returned user with out expires_in');
                    }

                    trace('oidc.signinSilent success!', result);
                    config.onTokenRenewed(adaptPublishedInfo(result as AuthenticatedUserWithProfile));

                    if (!isFreshRedirect) {
                        saveCurrentRoute();
                    }

                    return result;
                })
                .catch((error: unknown) => {
                    trace('oidc.signinSilent failed', error);

                    if (!isFreshRedirect) {
                        saveCurrentRoute();
                    }

                    return Promise.reject(error);
                });

        auth.events.addAccessTokenExpiring((...args) => {
            trace('oidc.accessTokenExpiring', ...args);
            trace('  automaticSilentRenew enabled, skipping manual silent renewal');
        });

        auth.events.addAccessTokenExpired((...args) => {
            trace('oidc.accessTokenExpired', ...args);
            config.onTokenExpired();
        });

        auth.events.addSilentRenewError(error => {
            trace('oidc.silentRenewError', error);

            if (isExpectedSilentRenewError(error)) {
                trace('oidc.silentRenewError expected auth/session error, expiring token only');
                config.onTokenExpired();
                return;
            }

            config.onSessionError(
                error instanceof Error ? error : new Error(getErrorMessage(error) || 'Silent renew failed')
            );
            config.onTokenExpired();
        });

        auth.events.addUserLoaded((...args) => {
            trace('oidc.userLoaded', ...args);
        });

        auth.events.addUserSignedOut((...args) => {
            trace('oidc.userSignedOut', ...args);
            config.onTokenExpired();
        });

        return trySignin().catch(error => {
            trace('oidc.signinSilent failed, trying page redirect...', error);

            const mightBeSuspicious = isFreshRedirect;

            if (runtimeConfig.login.preventRedirect) {
                // eslint-disable-next-line no-console
                console.warn('[feature/login] redirect prevented due to config. Error was', error);
            } else if (mightBeSuspicious) {
                trace('oidc.signinSilent.error', 'redirect prevented due to suspicious signin error', error);
                storage.discardRoute();
                config.onSessionError(
                    error instanceof Error ? error : new Error(getErrorMessage(error) || 'Need to sign in')
                );
            } else {
                saveCurrentRoute();
                void auth.signinRedirect();
            }

            return Promise.reject(new Error('Need to sign in'));
        });
    };
};

const runtimeAuth = configureAuth(window, import.meta.env);

if (env.featureToggles.tracing) {
    trace('runtimeAuth', runtimeAuth);
}

export const setupOAuth = configureSetupOAuth(runtimeAuth, configureStorage(getWindow()), getWindow(), import.meta.env);

export const mockOAuth = ({ onTokenRenewed }: { onTokenRenewed: (result: UserPublishedInfo) => unknown }) => {
    // eslint-disable-next-line no-console
    console.warn('[feature/login/oidc-session] Using mocked authorization due to config setting');

    onTokenRenewed(
        adaptPublishedInfo({
            access_token: (env.featureToggles.mockedToken as string) || 'valid-mocked-oauth-bogus-token',
            expires_in: 60 * 60 * 24 * 365,
            scope: 'openid',
            profile: {
                locale: env.runtimeConfig.login.mockLocale,
                account: '4c8e1f07-8510-4bbf-79ca-9d706675b9da',
            },
        })
    );

    return Promise.resolve();
};

export const configureRetrieveInitialState = (storage: RouteStorage) => () => ({
    initialRoute: storage.getRoute(),
});

export const retrieveInitialState = configureRetrieveInitialState(configureStorage(getWindow()));
